import React, { useState, useRef, useEffect } from 'react';
import { translate } from '../Services/translation';
import { useSelector, useDispatch } from 'react-redux';
import { toggle, update, receive, loadError } from '../Actions/Cart.action';
import MiniCartItem from './MiniCartItem';
import Notification from './Notification';
import { add } from '../Services/Cart.service';
import { catchError } from '../Actions/Error.action';
import WishlistButtonContainer from './WishlistButton.container';

const MiniCart = () => {
    const dispatch = useDispatch();
    const {
        quantity,
        orderTotal,
        checkoutUrl,
        showInfo,
        orderRows,
        deliveryCost,
        deliveryCostAsString,
    } = useSelector((state) => state.cart);

    const [isResizing, setIsResizing] = useState(false);
    const [latestRemoved, setlatestRemoved] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showSpinner, setShowSpinner] = useState('');
    const [showSignIn, setShowSignIn] = useState(false);
    const ref = useRef(null);
    const onToggle = () => dispatch(toggle());
    const mapCartToAbort = {};
    const loggedin = useSelector((state) => state.myPage).isLoggedIn;
    const registrationUrl =
        useSelector((state) => state.cart).registrationUrl || '';
    const loginUrl = useSelector((state) => state.cart).loginUrl || '';

    const onClickOutside = (element) => {
        element.classList.remove('mini-cart__info--visible');
        element.classList.add('mini-cart__info--hidden');
        onToggle();
    };

    useEffect(() => {
        let resizeTimer;
        const handleResizeStart = () => {
            setIsResizing(true);
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                setIsResizing(false);
            }, 200);
        };
        window.addEventListener('resize', handleResizeStart);

        return () => {
            window.removeEventListener('resize', handleResizeStart);
            clearTimeout(resizeTimer);
        };
    }, []);

    useEffect(() => {
        const minicartContainer = document.getElementById('minicart-container');
        const handleClickOutside = (event) => {
            if (
                ref.current &&
                !ref.current.contains(event.target) &&
                showInfo
            ) {
                onClickOutside && onClickOutside(minicartContainer);
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [showInfo]);

    const handleRemoveProduct = (orderItem) => {
        setShowSpinner(orderItem.articleNumber);
        setlatestRemoved(orderItem);
        setShowError(false);
        dispatch(
            update(
                orderItem.articleNumber,
                0,
                mapCartToAbort[orderItem.rowSystemId]
            )
        )
            .then(() => setShowNotification(true))
            .catch((ex) => {
                setShowError(true);
                dispatch(catchError(ex, (error) => loadError(error)));
                setTimeout(() => {
                    setShowError(false);
                }, '2500');
            })
            .finally(() => {
                setTimeout(() => {
                    setShowSpinner('');
                }, '2500');
            });
    };

    const undoRemoveProduct = () => {
        add({
            articleNumber: latestRemoved.articleNumber,
            quantity: latestRemoved.quantity,
        })
            .then((cart) => dispatch(receive(cart)))
            .catch((ex) => {
                setShowError(true);
                dispatch(catchError(ex, (error) => loadError(error)));
            });
        setShowNotification(false);
    };

    const handleCloseCart = () => {
        onToggle();
        setShowSignIn(false);
    };

    return (
        <div className="mini-cart">
            <a
                className="mini-cart__link--block"
                rel="nofollow"
                href="#"
                onClick={(e) => {
                    e.preventDefault();
                    onToggle();
                }}
            >
                <i className="mini-cart__icon">
                    <span className="mini-cart__quantity">{quantity}</span>
                </i>
            </a>
            <div
                className={`mini-cart__info 
                ${
                    !showInfo
                        ? 'mini-cart__info--hidden'
                        : 'mini-cart__info--visible'
                } 
                ${isResizing ? 'mini-cart__info--no-animation' : ''}`}
                ref={ref}
                id="minicart-container"
            >
                <div className="mini-cart__wrapper">
                    <div className="mini-cart__content-top">
                        <p className="mini-cart__title">
                            {translate(
                                !showSignIn
                                    ? 'minicart.title'
                                    : 'minicart.title.login'
                            )}
                        </p>
                        <span
                            className="mini-cart__close-button"
                            onClick={() => handleCloseCart()}
                        ></span>
                    </div>
                    {!showSignIn && orderRows.length < 1 && (
                        <div className="mini-cart__item">
                            <p>{translate('minicart.empty')}</p>
                        </div>
                    )}
                    {!showSignIn && (
                        <div className="mini-cart__item-container">
                            {orderRows &&
                                orderRows.map((orderItem, index) => (
                                    <MiniCartItem
                                        key={`mini-cart-item-${index}`}
                                        orderItem={orderItem}
                                        handleRemoveProduct={
                                            handleRemoveProduct
                                        }
                                        setShowError={setShowError}
                                        showSpinner={
                                            showSpinner ==
                                            orderItem.articleNumber
                                                ? true
                                                : false
                                        }
                                    />
                                ))}
                        </div>
                    )}
                    {!showSignIn ? (
                        <div className="mini-cart__sum">
                            {!showError &&
                                latestRemoved &&
                                showNotification && (
                                    <Notification
                                        info={true}
                                        text={`${
                                            latestRemoved.name
                                        } ${translate(
                                            'minicart.productremoved'
                                        )}`}
                                        buttonText={translate('minicart.undo')}
                                        onClick={() => undoRemoveProduct()}
                                    />
                                )}
                            {showError && (
                                <Notification
                                    text={translate('tooltip.error')}
                                />
                            )}
                            {orderRows && orderRows?.length > 0 && (
                                <div className="mini-cart__wishlist-sum">
                                    <WishlistButtonContainer
                                        label={translate(
                                            'minicart.savetowishlist'
                                        )}
                                        cssClass="mini-cart__add-to-wishlist"
                                        addCart={true}
                                    />
                                </div>
                            )}
                            <div className="mini-cart__total-sum">
                                <p>{translate('minicart.total')}</p>
                                <p>{orderTotal}</p>
                            </div>
                            {orderRows && orderRows?.length > 0 && (
                                <div className="mini-cart__info-sum">
                                    <p>{`${translate(
                                        'minicart.shippingcost'
                                    )}: `}</p>
                                    <p>
                                        {deliveryCost > 0
                                            ? deliveryCostAsString
                                            : translate(
                                                  'minicart.shippingcost-is-proposal'
                                              )}
                                    </p>
                                </div>
                            )}

                            <div className="mini-cart__checkout-sum">
                                <button
                                    onClick={() =>
                                        !loggedin && setShowSignIn(true)
                                    }
                                >
                                    <a href={loggedin && checkoutUrl}>
                                        {translate('minicart.checkout')}
                                    </a>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mini-cart__login">
                            <p className="mini-cart__login-text">
                                {translate('minicart.login.text')}
                            </p>
                            <ul>
                                {translate('minicart.login.listitem1') !== 'hide' && (
                                    <li className="mini-cart__login-listitem">
                                    <span className="mini-cart__login-listitem-icon"></span>
                                    <p>
                                        {translate('minicart.login.listitem1')}
                                    </p>
                                </li>
                                )}
                                {translate('minicart.login.listitem2') !== 'hide' && (
                                    <li className="mini-cart__login-listitem">
                                    <span className="mini-cart__login-listitem-icon"></span>
                                    <p>
                                        {translate('minicart.login.listitem2')}
                                    </p>
                                </li>
                                )}
                                {translate('minicart.login.listitem3') !== 'hide' && (
                                    <li className="mini-cart__login-listitem">
                                    <span className="mini-cart__login-listitem-icon"></span>
                                    <p>
                                        {translate('minicart.login.listitem3')}
                                    </p>
                                </li>
                                )}
                            </ul>
                            <a
                                href={loginUrl}
                                className="mini-cart__login-button"
                            >
                                {translate('minicart.login.loginbutton')}
                            </a>
                            <a
                                href={registrationUrl}
                                className="mini-cart__login-create-account-button"
                            >
                                {translate('minicart.login.createaccount')}
                            </a>
                            <div className="mini-cart__login-seperator">
                                <span className="mini-cart__login-seperator-text">
                                    {translate('minicart.login.or')}
                                </span>
                            </div>
                            <a
                                href={checkoutUrl + '?viewLandingPage=false'}
                                className="mini-cart__login-checkout"
                            >
                                {translate('minicart.login.continueasguest')}
                            </a>
                            <p className="mini-cart__login-checkout-text">
                                {translate('minicart.login.continuetext')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MiniCart;
